import { addMonths, differenceInMonths, format, isBefore } from 'date-fns'

import type { License } from '@img-arena/consoles-api-streaming'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import { GeoRegion, territories } from '@/components/forms/GeoBlockingTemplateForm'
import { CollapsibleRegionChips } from '@/components/inputs/common/CollapsibleRegionChips'
import { Column, COLUMN_CELL_TYPE, Filter } from '@/components/table/base'
import { FilterType } from '@/components/table/base/FilterableTable/FilterableTable.types'
import { DATE_FORMAT } from '@/constants/dateFormat'

export const LICENSE_STATUSES = {
  ACTIVE: 'active',
  FINISHED: 'finished',
  DEACTIVATED: 'deactivated',
  WARNING: 'warning'
} as const

type LicenseStatusType = (typeof LICENSE_STATUSES)[keyof typeof LICENSE_STATUSES]

const LICENSE_STATUS_COLORS: Record<
  LicenseStatusType,
  'error' | 'warning' | 'default' | 'primary'
> = {
  [LICENSE_STATUSES.DEACTIVATED]: 'error',
  [LICENSE_STATUSES.FINISHED]: 'primary',
  [LICENSE_STATUSES.WARNING]: 'warning',
  [LICENSE_STATUSES.ACTIVE]: 'default'
}

export type LicenseRow = {
  id: number
  propertyId: number
  propertyName: string
  sportName: string
  logoUrl: string | null
  year: number
  startDate: string
  endDate: string
  status: {
    type: LicenseStatusType
    label: string
  }
  licenseVolume: string
  maxEvents: string
  isDeleted: boolean
  isWhiteList: boolean
  geoList?: string
}

export function getStatus({ isDeleted, endDate }: { isDeleted: boolean; endDate: string }) {
  if (isDeleted) {
    return {
      type: LICENSE_STATUSES.DEACTIVATED,
      label: 'Deactivated'
    }
  }
  const now = new Date()
  const end = new Date(endDate)
  if (isBefore(end, now)) {
    return {
      type: LICENSE_STATUSES.FINISHED,
      label: 'Finished'
    }
  }
  const monthsLeft = differenceInMonths(addMonths(end, 1), now)
  if (monthsLeft <= 1) {
    return {
      type: LICENSE_STATUSES.ACTIVE,
      label: '< 1 mo'
    }
  } else if (monthsLeft > 11) {
    const extraMonths = monthsLeft % 12
    return {
      type: LICENSE_STATUSES.ACTIVE,
      label: `< 1 yrs${extraMonths > 0 ? ` ${extraMonths} mo` : ''}`
    }
  } else {
    return {
      type: LICENSE_STATUSES.ACTIVE,
      label: `< ${monthsLeft} mo`
    }
  }
}

export const DGE_TABLE_COLUMNS: Column<LicenseRow>[] = [
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'logoUrl',
    label: '',
    sx: {
      padding: 2
    },
    render: (value) =>
      value && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 32,
            width: 32,
            background: '#fff',
            borderRadius: '50%',
            overflow: 'hidden',
            justifyContent: 'center'
          }}
        >
          <img
            src={value}
            alt="Property Logo"
            style={{
              height: 32,
              maxWidth: 32,
              padding: 2,
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Box>
      )
  },
  {
    type: COLUMN_CELL_TYPE.NUMBER,
    id: 'id',
    label: 'ID',
    sortAttribute: 'id'
  },
  {
    type: COLUMN_CELL_TYPE.NUMBER,
    id: 'year',
    label: 'Year',
    sortAttribute: 'year'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'startDate',
    label: 'Start Date',
    sortAttribute: 'startDate'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'endDate',
    label: 'End Date',
    sortAttribute: 'endDate'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'propertyName',
    label: 'Property Name',
    sortAttribute: 'propertyName'
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: '$geoList',
    label: 'Territories',

    render: (row) => {
      if (!row.geoList) {
        return null
      }

      const geoList = JSON.parse(row.geoList) || []
      const selectedRegions = geoList
        ? geoList
            .map((code: string) => territories.find((territory) => territory.iso2 === code))
            .filter((region: GeoRegion): region is GeoRegion => region !== undefined)
        : []

      return (
        <CollapsibleRegionChips
          regions={selectedRegions}
          isWhiteList={row.isWhiteList}
          maxVisible={2}
        />
      )
    }
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'sportName',
    label: 'Sport',
    render: (value) => {
      return <Chip label={value} size="small" />
    },
    sortAttribute: 'sportName',
    comparator: (a, b) => {
      return (a ?? '').localeCompare(b ?? '')
    }
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'licenseVolume',
    label: 'License Volume',
    sortAttribute: 'licenseVolume'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'maxEvents',
    label: 'Max Events',
    sortAttribute: 'maxEvents'
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'status',
    label: 'License Status',
    render: (value) => {
      return (
        <Chip
          label={value.label}
          size="small"
          color={LICENSE_STATUS_COLORS[value.type]}
          variant="outlined"
        />
      )
    }
  }
]

const getOptions = (data: LicenseRow[]) => {
  const propertyMap = new Map<string, FilterType>()
  const statusMap = new Map<string, FilterType>()
  const sportMap = new Map<string, FilterType>()

  data.forEach((item) => {
    if (item.propertyName && !propertyMap.has(item.propertyName)) {
      propertyMap.set(item.propertyName, {
        value: item.propertyName,
        label: item.propertyName
      })
    }
    if (item.status.type && !statusMap.has(item.status.type)) {
      statusMap.set(item.status.type, {
        value: item.status.type,
        label: item.status.type.charAt(0).toUpperCase() + item.status.type.slice(1)
      })
    }
    if (item.sportName && !sportMap.has(item.sportName)) {
      sportMap.set(item.sportName, {
        value: item.sportName,
        label: item.sportName
      })
    }
  })

  const sortByLabel = (arr: FilterType[]) =>
    arr.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))

  return {
    propertyOptions: sortByLabel(Array.from(propertyMap.values())),
    statusOptions: sortByLabel(Array.from(statusMap.values())),
    sportOptions: sortByLabel(Array.from(sportMap.values()))
  }
}

export const getDGETableFilters = (data: LicenseRow[]): Filter<LicenseRow>[] => {
  const { propertyOptions, statusOptions, sportOptions } = getOptions(data)

  return [
    {
      //TODO: create a ticket to refactor all filter type in filterable table to be used from a constant
      type: 'freeTextSearch',
      paramName: 'ft',
      placeholder: 'Search',
      filterOptions: (row, search) => {
        if (!search) return true
        const lowerCaseSearch = search.toLowerCase()
        return (
          row.propertyName.toLowerCase().includes(lowerCaseSearch) ||
          row.sportName.toLowerCase().includes(lowerCaseSearch) ||
          String(row.id).toLowerCase().includes(lowerCaseSearch) ||
          String(row.propertyId).toLowerCase().includes(lowerCaseSearch)
        )
      }
    },
    {
      type: 'select',
      paramName: 'property',
      label: 'Property',
      options: propertyOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        return item.propertyName === filter.value
      }
    },
    {
      type: 'select',
      paramName: 'sport',
      label: 'Sport',
      options: sportOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        return item.sportName === filter.value
      }
    },
    {
      type: 'select',
      paramName: 'status',
      label: 'Status',
      options: statusOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        return item.status.type === filter.value
      }
    }
  ]
}

export function getLicenseTableRows(data: License[]): LicenseRow[] {
  return data.reduce<LicenseRow[]>((acc, item) => {
    const currentPeriod = item.periods?.find((p) => p.id === item.currentLicensingPeriodId)
    const period =
      currentPeriod || (item.periods?.length ? item.periods[item.periods.length - 1] : undefined)
    if (!period) return acc

    const year = period.start ? new Date(period.start).getFullYear() : 0
    const startDate = format(period.start.split('T')[0], DATE_FORMAT.DATE) || ''
    const endDate = format(period.end.split('T')[0], DATE_FORMAT.DATE) || ''
    const propertyId = item.property?.id ?? 0
    const propertyName = item.property?.name ?? ''
    const sportName = item.property?.sport?.name ?? ''
    const logoUrl = item.property?.logoUrl ?? null
    const isDeleted = Boolean(item.isDeleted)
    const status = getStatus({ isDeleted, endDate })
    const isWhiteList = item.isWhiteList
    const geoList = item.geoListField
    const licenseVolume =
      item.purchasedEvents === null ? 'Unlimited' : String(item.purchasedEvents ?? '')
    let maxEvents = ''
    if (typeof item.maxEvents === 'number') {
      maxEvents = item.maxEvents === -1 ? 'Autobooking' : String(item.maxEvents)
    } else {
      maxEvents = ''
    }

    acc.push({
      id: item.id,
      year,
      startDate,
      endDate,
      propertyId,
      propertyName,
      sportName,
      logoUrl,
      status,
      licenseVolume,
      maxEvents,
      isDeleted,
      isWhiteList,
      geoList
    })
    return acc
  }, [])
}
